/**
 * core/hooks/useMondayData.ts
 *
 * React Query hooks that wrap all monday.com data fetch functions.
 * Components import these instead of calling query functions directly.
 *
 * Query key conventions:
 *   ['me']
 *   ['boards', limit]
 *   ['board', boardId, 'items', limit]
 *   ['myTasks', limit]
 *   ['inbox', limit]
 *   ['boardHealth', boardId]
 *   ['search', term, limit]
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  queryMe,
  queryAllBoards,
  queryBoardItems,
  queryMyTasks,
  queryInbox,
  queryBoardHealth,
  queryCrossBoardSearch,
} from '@core/api/queries';
import type {
  MondayItem,
  MondayUser,
  MondayBoard,
  MondayNotification,
  BoardHealthMetrics,
  SearchResult,
  Action,
} from '@core/types';

// ---------------------------------------------------------------------------
// Current user
// ---------------------------------------------------------------------------

export function useMe(): UseQueryResult<MondayUser> {
  return useQuery({
    queryKey: ['me'],
    queryFn: queryMe,
    staleTime: 10 * 60 * 1000, // user info rarely changes — 10 min
  });
}

// ---------------------------------------------------------------------------
// Boards
// ---------------------------------------------------------------------------

export function useBoards(limit = 50): UseQueryResult<MondayBoard[]> {
  return useQuery({
    queryKey: ['boards', limit],
    queryFn: () => queryAllBoards(limit),
    staleTime: 5 * 60 * 1000,
  });
}

export function useBoardItems(
  boardId: string | undefined,
  limit = 100,
): UseQueryResult<MondayItem[]> {
  return useQuery({
    queryKey: ['board', boardId, 'items', limit],
    queryFn: () => queryBoardItems(boardId!, limit),
    enabled: Boolean(boardId),
  });
}

// ---------------------------------------------------------------------------
// My Work
// ---------------------------------------------------------------------------

export function useMyTasks(limit = 50): UseQueryResult<Action[]> {
  return useQuery({
    queryKey: ['myTasks', limit],
    queryFn: () => queryMyTasks(limit),
  });
}

// ---------------------------------------------------------------------------
// Inbox
// ---------------------------------------------------------------------------

export function useInbox(limit = 50): UseQueryResult<MondayNotification[]> {
  return useQuery({
    queryKey: ['inbox', limit],
    queryFn: () => queryInbox(limit),
    staleTime: 60 * 1000,          // inbox changes often
    refetchInterval: 2 * 60 * 1000, // poll every 2 min while mounted
  });
}

// ---------------------------------------------------------------------------
// Board Health
// ---------------------------------------------------------------------------

export function useBoardHealth(
  boardId: string | undefined,
): UseQueryResult<BoardHealthMetrics> {
  return useQuery({
    queryKey: ['boardHealth', boardId],
    queryFn: () => queryBoardHealth(boardId!),
    enabled: Boolean(boardId),
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Cross-board search
// ---------------------------------------------------------------------------

export function useCrossBoardSearch(
  term: string,
  limit = 20,
): UseQueryResult<SearchResult[]> {
  return useQuery({
    queryKey: ['search', term, limit],
    queryFn: () => queryCrossBoardSearch(term, limit),
    enabled: term.trim().length >= 2,
    staleTime: 30 * 1000,
    retry: false, // search failures are best-effort, no retry
  });
}

// ---------------------------------------------------------------------------
// Cache invalidation helpers (for use after mutations)
// ---------------------------------------------------------------------------

export function useInvalidateBoard() {
  const qc = useQueryClient();
  return (boardId: string) => {
    void qc.invalidateQueries({ queryKey: ['board', boardId] });
  };
}

export function useInvalidateMyTasks() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['myTasks'] });
  };
}

export function useInvalidateInbox() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['inbox'] });
  };
}

// Re-export useMutation for convenience in component files
export { useMutation };
